import { c as _c } from "react/compiler-runtime";
import { IconButton } from "@/components/ui/IconButton";
import { cva } from "class-variance-authority";
import { XIcon } from "lucide-react";
import * as React from "react";
import cn from "twsx";

// Linear tag: 18px, radius 4, 11/500 label on the hue's bg-subtle fill. The
// colour names the meaning; the hue ramp comes from design-system tokens.
// Warning has no warm fill: a neutral tag with a gold status dot.
// `wrap` lets long values (email addresses, filter values) break onto more
// lines; `onRemove` adds the remove control used by selected-value chips.
// `size="control"` is the 28px status chip that sits beside buttons;
// `size="icon"` is the round 18px glyph-only status mark; `size="tile"` is
// the 28px square type glyph that leads a list row, tinted by status.
const badgeVariants = cva("inline-flex min-w-0 max-w-full shrink-0 items-center gap-1 whitespace-nowrap font-medium leading-none [&>svg]:shrink-0", {
  variants: {
    color: {
      neutral: "bg-bg-secondary/70 text-text-secondary",
      secondary: "bg-bg-secondary/70 text-text-secondary",
      primary: "bg-blue-bg-subtle text-blue-text",
      info: "bg-blue-bg-subtle text-blue-text",
      success: "bg-green-bg-subtle text-green-text",
      warning: ["bg-bg-secondary/70 text-text-secondary", "before:size-1.5 before:shrink-0 before:rounded-full before:bg-yellow-light"],
      danger: "bg-red-bg-subtle text-red-text"
    },
    size: {
      md: "h-4.5 rounded-kbd px-1.5 text-xs [&>svg]:size-3",
      control: "h-control-md rounded-kbd px-2.5 text-xs [&>svg]:size-3.5",
      icon: "size-4.5 justify-center rounded-full px-0 [&>svg]:size-2.5",
      tile: "size-7 justify-center rounded-md px-0 [&>svg]:size-4"
    },
    wrap: {
      true: "h-auto min-h-4.5 whitespace-normal py-0.75 leading-3 [overflow-wrap:anywhere]",
      false: ""
    },
    removable: {
      true: "pr-0.5",
      false: ""
    }
  },
  defaultVariants: {
    color: "neutral",
    size: "md",
    wrap: false,
    removable: false
  }
});
export type BadgeColor = "primary" | "secondary" | "danger" | "success" | "warning" | "info" | "neutral";
export type BadgeProps = {
  color?: BadgeColor;
  size?: "md" | "control" | "icon" | "tile";
  wrap?: boolean;
  onRemove?: () => void;
  removeLabel?: string;
  ref?: React.Ref<HTMLDivElement>;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "color">;

/** Use when labelling a status, category or count inline as a tag. */
export default function Badge(t0) {
  const $ = _c(30);
  let children;
  let className;
  let color;
  let onRemove;
  let props;
  let ref;
  let size;
  let t1;
  let wrap;
  if ($[0] !== t0) {
    ({
      className,
      color,
      size,
      wrap,
      onRemove,
      removeLabel: t1,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = color;
    $[4] = onRemove;
    $[5] = props;
    $[6] = ref;
    $[7] = size;
    $[8] = t1;
    $[9] = wrap;
  } else {
    children = $[1];
    className = $[2];
    color = $[3];
    onRemove = $[4];
    props = $[5];
    ref = $[6];
    size = $[7];
    t1 = $[8];
    wrap = $[9];
  }
  const removeLabel = t1 === undefined ? "Remove" : t1;
  let t2;
  if ($[10] !== className || $[11] !== color || $[12] !== onRemove || $[13] !== size || $[14] !== wrap) {
    t2 = cn(badgeVariants({
      color,
      size,
      wrap,
      removable: Boolean(onRemove)
    }), className);
    $[10] = className;
    $[11] = color;
    $[12] = onRemove;
    $[13] = size;
    $[14] = wrap;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== children || $[17] !== wrap) {
    let t4;
    if ($[19] !== wrap) {
      t4 = child => !wrap && (typeof child === "string" || typeof child === "number") ? <span className="min-w-0 truncate">{child}</span> : child;
      $[19] = wrap;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    t3 = React.Children.map(children, t4);
    $[16] = children;
    $[17] = wrap;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[21] !== onRemove || $[22] !== removeLabel) {
    t4 = onRemove && <IconButton label={removeLabel} variant="chip" size={14} showTooltip={false} title={removeLabel} onMouseDown={_temp} onClick={event_0 => {
      event_0.preventDefault();
      event_0.stopPropagation();
      onRemove();
    }}><XIcon aria-hidden="true" /></IconButton>;
    $[21] = onRemove;
    $[22] = removeLabel;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== props || $[25] !== ref || $[26] !== t2 || $[27] !== t3 || $[28] !== t4) {
    t5 = <div ref={ref} className={t2} {...props}>{t3}{t4}</div>;
    $[24] = props;
    $[25] = ref;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  return t5;
}
function _temp(event) {
  event.preventDefault();
  event.stopPropagation();
}
export { Badge, badgeVariants };